// Sentry initialization - must be before other imports
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';

Sentry.init(
  {
    dsn: "https://4daeac2699495eb64b087f68ced56664@o4508576514703360.ingest.de.sentry.io/4509514198089808",
    integrations: [
      SentryReact.browserTracingIntegration()
    ]
  },
  // Forward the init method from @sentry/react
  SentryReact.init
);

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
// Redesign fonts (self-hosted, offline-safe): DM Sans = UI text, DM Mono = numeric/financial values.
// DM Mono only ships weights 300/400/500.
import '@fontsource/dm-sans/400.css'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/600.css'
import '@fontsource/dm-sans/700.css'
import '@fontsource/dm-mono/400.css'
import '@fontsource/dm-mono/500.css'
import './index.css'
// Note: You can call PurchaseService.debugPackages() from browser console for debugging
import './i18n/config' // Initialize i18n
import { setupGlobalErrorLogging, logError } from './utils/errorLogger'
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { isNativePlatform } from '@/utils/platform';

// Set up global error capture
setupGlobalErrorLogging();

// Define StatusBar interface for Cordova/Capacitor
declare global {
  interface Window {
    StatusBar?: {
      overlaysWebView: (overlay: boolean) => void;
      styleDefault: () => void;
      styleLightContent: () => void;
      styleBlackTranslucent: () => void;
      styleBlackOpaque: () => void;
      backgroundColorByName: (colorName: string) => void;
      backgroundColorByHexString: (hexString: string) => void;
      hide: () => void;
      show: () => void;
      isVisible: boolean;
    };
  }
}

// Simple error handling for React initialization
window.addEventListener('error', (event) => {
  if (event.error && event.error.message && event.error.message.includes('Minified React error')) {
    
  }
});

// Configure status bar for fullscreen usage
document.addEventListener('deviceready', () => {
  if (typeof window.StatusBar !== 'undefined') {
    window.StatusBar.overlaysWebView(true);
    window.StatusBar.backgroundColorByHexString('#082D58'); // brand-900 navy header
    window.StatusBar.styleLightContent();
  }
}, false);

// Development-only utilities (simplified)
if (import.meta.env.DEV) {
  // Simple debugging utilities
  if (typeof window !== 'undefined') {
    (window as any).debugSupabase = {
      envVars: {
        url: import.meta.env.VITE_SUPABASE_URL,
        keyPresent: !!import.meta.env.VITE_SUPABASE_ANON_KEY
      }
    };
  }
}

// Set up app resume listener for notifications (simplified)
if (isNativePlatform()) {
  CapApp.addListener('resume', async () => {
    try {
      const { syncNotificationPermissions } = await import('@/integrations/onesignal');
      await syncNotificationPermissions();
    } catch (error) {
      
    }
  });
}

// Simple React app rendering
const renderApp = () => {
  try {
    const rootElement = document.getElementById("root");
    
    if (!rootElement) {
      const newRoot = document.createElement('div');
      newRoot.id = 'root';
      document.body.appendChild(newRoot);
      ReactDOM.createRoot(newRoot).render(<App />);
    } else {
      ReactDOM.createRoot(rootElement).render(<App />);
    }
  } catch (error) {
    
  }
};

// Render when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderApp);
} else {
  renderApp();
}
